import { ListPlus } from 'lucide-react';
import { BlocoCampos, CampoArea, Interruptor } from '../componentes/campos';
import { ListaEditavel, ListaTextos } from '../componentes/lista-editavel';
import { CATALOGO_PERIODICIDADE } from '../manual-catalogos';
import { falhasPadrao, novaFalha, novaLubrificacao, novaManutencao, novoTexto, planoManutencaoPadrao } from '../manual-defaults';
import { TEXTOS } from '../manual-textos';
import type { ItemFalha, ItemLubrificacao, ItemManutencao } from '../manual-types';
import type { PropsSecao } from './tipos';

const PERIODICIDADES = CATALOGO_PERIODICIDADE.map((valor) => ({ valor, rotulo: valor }));

export function SecaoManutencao({ editor }: PropsSecao) {
  const { dados, definirCampo, definirOpcao, adicionarItem, adicionarItens, atualizarItem, removerItem, moverItem } = editor;

  // Manuais criados antes do modelo vir preenchido (ou em que tudo foi apagado)
  // recuperam as linhas padrão; o que já existe com o mesmo texto não duplica.
  const inserirPlanoPadrao = () => {
    const existentes = new Set(dados.planoManutencao.map((item) => item.tarefa.trim()));
    adicionarItens('planoManutencao', planoManutencaoPadrao().filter((item) => !existentes.has(item.tarefa)));
  };
  const inserirFalhasPadrao = () => {
    const existentes = new Set(dados.falhas.map((item) => `${item.falha.trim()}|${item.causa.trim()}`));
    adicionarItens('falhas', falhasPadrao().filter((item) => !existentes.has(`${item.falha}|${item.causa}`)));
  };

  return (
    <>
      <BlocoCampos titulo="Orientação geral" descricao="Abertura do capítulo 6 — regra de bloqueio antes de qualquer intervenção.">
        <CampoArea
          rotulo="Texto de abertura"
          linhas={4}
          valor={dados.orientacaoManutencao}
          placeholder={TEXTOS.manutencaoIntro}
          aoMudar={(valor) => definirCampo('orientacaoManutencao', valor)}
        />
      </BlocoCampos>

      <BlocoCampos
        titulo="Plano de manutenção preventiva"
        descricao="Vem preenchido com o plano do modelo: apague a linha que não se aplica a esta máquina e acrescente o que for específico (item 6.1)."
        acoes={(
          <button type="button" className="manual-ghost-button" onClick={inserirPlanoPadrao}>
            <ListPlus />Inserir linhas do modelo
          </button>
        )}
      >
        <ListaEditavel<ItemManutencao>
          itens={dados.planoManutencao}
          colunas={[
            { chave: 'parte', rotulo: 'Parte / conjunto', placeholder: 'Ex.: Unidade hidráulica' },
            { chave: 'tarefa', rotulo: 'Tarefa', placeholder: 'Ex.: Verificar nível de óleo', largura: '1.3fr' },
            { chave: 'metodo', rotulo: 'Método', placeholder: 'Ex.: Inspeção visual pelo visor', largura: '1.3fr' },
            { chave: 'periodicidade', rotulo: 'Periodicidade', tipo: 'selecao', opcoes: PERIODICIDADES, largura: '0.9fr' },
            { chave: 'responsavel', rotulo: 'Responsável', placeholder: 'Ex.: Manutenção', largura: '0.8fr' },
          ]}
          rotuloAdicionar="Adicionar tarefa"
          vazio="Nenhuma tarefa cadastrada."
          aoAdicionar={() => adicionarItem('planoManutencao', novaManutencao())}
          aoAtualizar={(id, patch) => atualizarItem('planoManutencao', id, patch)}
          aoRemover={(id) => removerItem('planoManutencao', id)}
          aoMover={(id, direcao) => moverItem('planoManutencao', id, direcao)}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Plano de lubrificação" descricao="Item opcional — ative quando a máquina possuir pontos de lubrificação definidos.">
        <Interruptor
          rotulo="Incluir plano de lubrificação"
          ativo={dados.opcoes.incluirLubrificacao}
          aoMudar={(ativo) => definirOpcao('incluirLubrificacao', ativo)}
        />
        {dados.opcoes.incluirLubrificacao && (
          <ListaEditavel<ItemLubrificacao>
            itens={dados.lubrificacao}
            colunas={[
              { chave: 'ponto', rotulo: 'Ponto', placeholder: 'Ex.: Guias da coluna' },
              { chave: 'produto', rotulo: 'Produto', placeholder: 'Ex.: Graxa NLGI 2' },
              { chave: 'quantidade', rotulo: 'Quantidade', placeholder: 'Ex.: 2 bicadas', largura: '0.8fr' },
              { chave: 'periodicidade', rotulo: 'Periodicidade', tipo: 'selecao', opcoes: PERIODICIDADES, largura: '0.9fr' },
            ]}
            rotuloAdicionar="Adicionar ponto"
            vazio="Nenhum ponto de lubrificação cadastrado."
            aoAdicionar={() => adicionarItem('lubrificacao', novaLubrificacao())}
            aoAtualizar={(id, patch) => atualizarItem('lubrificacao', id, patch)}
            aoRemover={(id) => removerItem('lubrificacao', id)}
            aoMover={(id, direcao) => moverItem('lubrificacao', id, direcao)}
          />
        )}
      </BlocoCampos>

      <BlocoCampos titulo="Limpeza" descricao="Rotinas de limpeza permitidas e restrições.">
        <ListaTextos
          itens={dados.limpeza}
          multilinha
          placeholder="Ex.: Remover cavacos da mesa com pincel, com a máquina desenergizada."
          rotuloAdicionar="Adicionar rotina"
          vazio="Nenhuma rotina de limpeza cadastrada."
          aoAdicionar={() => adicionarItem('limpeza', novoTexto())}
          aoAtualizar={(id, patch) => atualizarItem('limpeza', id, patch)}
          aoRemover={(id) => removerItem('limpeza', id)}
          aoMover={(id, direcao) => moverItem('limpeza', id, direcao)}
          sugestoes={[
            'Remover resíduos e cavacos ao final de cada turno.',
            'Não utilizar jato de água direto sobre painéis e sensores.',
            'Manter a sinalização e as janelas de inspeção limpas e legíveis.',
            'Recolher resíduos oleosos em recipiente identificado.',
          ]}
          aoEscolherSugestao={(valor) => adicionarItem('limpeza', novoTexto(valor))}
        />
      </BlocoCampos>

      <BlocoCampos
        titulo="Lista de falhas"
        descricao="Vem preenchida com as falhas do modelo: apague o que não se aplica e acrescente as falhas próprias desta máquina."
        acoes={(
          <button type="button" className="manual-ghost-button" onClick={inserirFalhasPadrao}>
            <ListPlus />Inserir linhas do modelo
          </button>
        )}
      >
        <ListaEditavel<ItemFalha>
          itens={dados.falhas}
          colunas={[
            { chave: 'falha', rotulo: 'Falha', placeholder: 'Ex.: Máquina não inicia o ciclo' },
            { chave: 'causa', rotulo: 'Causa provável', placeholder: 'Ex.: Proteção aberta', largura: '1.2fr' },
            { chave: 'solucao', rotulo: 'Solução', placeholder: 'Ex.: Fechar a proteção e rearmar o comando', largura: '1.6fr', tipo: 'area' },
          ]}
          rotuloAdicionar="Adicionar falha"
          vazio="Nenhuma falha cadastrada."
          aoAdicionar={() => adicionarItem('falhas', novaFalha())}
          aoAtualizar={(id, patch) => atualizarItem('falhas', id, patch)}
          aoRemover={(id) => removerItem('falhas', id)}
          aoMover={(id, direcao) => moverItem('falhas', id, direcao)}
        />
      </BlocoCampos>
    </>
  );
}
